import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { findProfileByEmail, linkDiscipleToPair, unlinkDisciple, updatePairStatus } from '../supabase/api';

const { FiUsers, FiSearch, FiCheckCircle, FiAlertCircle, FiUserX, FiUserCheck, FiMail, FiLoader } = FiIcons;

function LinkedParticipantCard({ participant, pairId, onUnlink, onArchive }) {
  const [action, setAction] = useState(null); // 'unlink' | 'archive' | null
  const [processing, setProcessing] = useState(false);

  const handleConfirm = async () => {
    setProcessing(true);
    try {
      if (action === 'unlink') {
        await unlinkDisciple(pairId);
        onUnlink();
      } else if (action === 'archive') {
        await updatePairStatus(pairId, 'completed');
        onArchive();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setProcessing(false);
      setAction(null);
    }
  };

  return (
    <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
          <SafeIcon icon={FiUserCheck} className="w-5 h-5 text-green-600 dark:text-green-400" />
        </div>
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{participant.name || 'Unnamed Participant'}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{participant.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!action ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAction('archive')}
              className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 px-3 py-1.5 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors font-medium"
            >
              Archive Journey
            </button>
            <button
              onClick={() => setAction('unlink')}
              className="text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-3 py-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
            >
              Unlink
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600 dark:text-gray-300">
              {action === 'archive' ? 'Archive this journey?' : 'Are you sure?'}
            </span>
            <button
              onClick={handleConfirm}
              disabled={processing}
              className={`text-xs text-white px-3 py-1.5 rounded-lg disabled:opacity-60 transition-colors flex items-center gap-1 ${
                action === 'archive' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-500 hover:bg-red-600'
              }`}
            >
              {processing && <span className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />}
              Yes, {action}
            </button>
            <button
              onClick={() => setAction(null)}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function LinkParticipantPanel({ leaderId, pair, onPairUpdate }) {
  const [email, setEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [found, setFound] = useState(null); // profile found
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const hasParticipant = pair?.disciple_id && pair?.disciple;

  const reset = () => {
    setEmail('');
    setFound(null);
    setNotFound(false);
    setError('');
    setSuccess('');
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSearching(true);
    setFound(null);
    setNotFound(false);
    setError('');
    setSuccess('');

    try {
      const profile = await findProfileByEmail(email);
      if (profile) {
        setFound(profile);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      setError(err.message || 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleLink = async () => {
    if (!found) return;
    setLinking(true);
    setError('');

    try {
      const updatedPair = await linkDiscipleToPair(leaderId, found.id);
      setSuccess(`${found.name || found.email} has been linked as your participant!`);
      setFound(null);
      setEmail('');
      setNotFound(false);
      onPairUpdate(updatedPair);
    } catch (err) {
      setError(err.message || 'Failed to link participant.');
    } finally {
      setLinking(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 bg-primary bg-opacity-10 dark:bg-opacity-20 rounded-lg flex items-center justify-center">
          <SafeIcon icon={FiUsers} className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-space font-semibold text-gray-900 dark:text-white">My Participant</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Link a participant to your discipleship journey</p>
        </div>
      </div>

      {hasParticipant ? (
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Currently linked:</p>
          <LinkedParticipantCard
            participant={pair.disciple}
            pairId={pair.id}
            onUnlink={() => onPairUpdate({ ...pair, disciple_id: null, disciple: null })}
            onArchive={() => onPairUpdate(null)}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded-lg flex items-start gap-2">
            <SafeIcon icon={FiMail} className="w-4 h-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Ask your participant to <strong>sign up first</strong>, then enter their email below to link their account.
            </p>
          </div>

          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setFound(null);
                  setNotFound(false);
                  setError('');
                }}
                placeholder="participant@email.com"
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white dark:placeholder-gray-500 rounded-lg text-sm custom-focus transition-colors"
                required
              />
            </div>
            <button
              type="submit"
              disabled={searching || !email.trim()}
              className="bg-primary text-white px-4 py-2.5 rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2 text-sm disabled:opacity-60"
            >
              {searching ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" /> : <SafeIcon icon={FiSearch} className="w-4 h-4" />}
              Search
            </button>
          </form>

          <AnimatePresence mode="wait">
            {found && (
              <motion.div
                key="found"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-xl flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiUserCheck} className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{found.name || 'Unnamed'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{found.email} · Participant account</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={reset}
                    className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 px-2 py-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLink}
                    disabled={linking}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-60"
                  >
                    {linking ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5" />}
                    Link Participant
                  </button>
                </div>
              </motion.div>
            )}

            {notFound && (
              <motion.div
                key="notfound"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-lg flex items-start gap-2"
              >
                <SafeIcon icon={FiUserX} className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">No participant account found</p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-0.5">
                    Make sure your participant has signed up with that email and selected <strong>Participant</strong> as their role.
                  </p>
                </div>
              </motion.div>
            )}

            {error && (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 rounded-lg flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
              >
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 flex-shrink-0" />
                {error}
              </motion.div>
            )}

            {success && (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg flex items-center gap-2 text-sm text-green-700 dark:text-green-400"
              >
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4 flex-shrink-0" />
                {success}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
}

export default LinkParticipantPanel;